/**
 * Variables de paie — monthly per-employee table (hours pointed + hourly rate
 * + gross salary). Lets the admin download the data as Excel or email it to
 * the configured comptable in one click.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileXls, PaperPlaneTilt, Clock, CurrencyEur, Users } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => (n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (n) => (n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const todayYM = () => {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

const PayrollVariablesPage = () => {
  const [period, setPeriod] = useState(todayYM());
  const [data, setData] = useState({ rows: [], total_hours: 0, total_gross: 0, comptable_email: '' });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [overrideEmail, setOverrideEmail] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/payroll/variables`, {
        params: { year: period.year, month: period.month },
        withCredentials: true,
      });
      setData(res.data);
      setOverrideEmail(res.data.comptable_email || '');
    } catch (e) {
      toast.error('Erreur chargement variables de paie');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleMonthChange = (e) => {
    const [y, m] = e.target.value.split('-');
    setPeriod({ year: parseInt(y, 10), month: parseInt(m, 10) });
  };

  const downloadExcel = () => {
    window.open(`${API_URL}/api/payroll/variables/export?year=${period.year}&month=${period.month}`, '_blank');
  };

  const sendToComptable = async () => {
    if (!overrideEmail) {
      toast.error("Renseignez l'email comptable d'abord");
      return;
    }
    if (!window.confirm(`Envoyer les variables de paie ${String(period.month).padStart(2, '0')}/${period.year} a ${overrideEmail} ?`)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/payroll/send-to-comptable`, {
        year: period.year, month: period.month, email: overrideEmail,
      }, { withCredentials: true });
      toast.success(`Variables envoyees a ${res.data.to} (${res.data.rows_count} salaries)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur envoi");
    } finally {
      setSending(false);
    }
  };

  const monthValue = `${period.year}-${String(period.month).padStart(2, '0')}`;
  const monthLabel = new Date(period.year, period.month - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  return (
    <div data-testid="payroll-variables-page">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input
            type="month"
            value={monthValue}
            onChange={handleMonthChange}
            className="form-input"
            data-testid="payroll-month-input"
            style={{ height: 38, width: 180 }}
          />
          <span style={{ color: '#5E5F66', textTransform: 'capitalize' }}>{monthLabel}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="email"
            value={overrideEmail}
            onChange={(e) => setOverrideEmail(e.target.value)}
            placeholder="email du comptable"
            className="form-input"
            data-testid="payroll-comptable-email"
            style={{ height: 38, width: 240 }}
          />
          <button
            type="button"
            onClick={downloadExcel}
            className="btn btn-secondary"
            data-testid="payroll-download-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <FileXls size={18} /> Excel
          </button>
          <button
            type="button"
            onClick={sendToComptable}
            disabled={sending || !overrideEmail}
            className="btn btn-primary"
            data-testid="payroll-send-comptable-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <PaperPlaneTilt size={18} /> {sending ? 'Envoi…' : 'Envoyer au comptable'}
          </button>
        </div>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 18 }}>
        <div className="stat-card">
          <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Users size={14} /> Salaries</div>
          <div className="stat-value" style={{ fontSize: '1.4rem' }} data-testid="payroll-count">{data.rows?.length || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Clock size={14} /> Heures pointees</div>
          <div className="stat-value" style={{ fontSize: '1.4rem', color: '#1565C0' }} data-testid="payroll-total-hours">{fmtInt(data.total_hours)} h</div>
        </div>
        <div className="stat-card">
          <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><CurrencyEur size={14} /> Brut total</div>
          <div className="stat-value" style={{ fontSize: '1.4rem', color: '#2E7D32' }} data-testid="payroll-total-gross">{fmt(data.total_gross)} EUR</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Detail par salarie</h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#5E5F66' }}>Chargement…</div>
          ) : (
            <table className="data-table" data-testid="payroll-table">
              <thead>
                <tr>
                  <th>Salarie</th>
                  <th>Poste</th>
                  <th>Contrat</th>
                  <th style={{ textAlign: 'right' }}>Heures pointees</th>
                  <th style={{ textAlign: 'right' }}>Taux horaire</th>
                  <th style={{ textAlign: 'right' }}>Salaire brut</th>
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).map((r) => (
                  <tr key={r.user_id} data-testid={`payroll-row-${r.user_id}`}>
                    <td style={{ fontWeight: 600 }}>{r.name}</td>
                    <td style={{ color: '#5E5F66' }}>{r.job_title || '-'}</td>
                    <td style={{ color: '#5E5F66' }}>{r.contract_type || '-'}</td>
                    <td style={{ textAlign: 'right' }}>{fmtInt(r.hours_approved)} h</td>
                    <td style={{ textAlign: 'right' }}>
                      {r.hourly_rate > 0
                        ? <span>{fmt(r.hourly_rate)} EUR/h</span>
                        : <span style={{ color: '#C62828', fontStyle: 'italic' }} title="Renseignez le taux horaire sur la fiche">manquant</span>}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#2E7D32' }}>{fmt(r.gross_salary)} EUR</td>
                  </tr>
                ))}
                {(!data.rows || data.rows.length === 0) && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#9E9EA7' }}>Aucun salarie</td></tr>
                )}
              </tbody>
              {data.rows && data.rows.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid #E2E2E6', fontWeight: 700 }}>
                    <td colSpan={3}>TOTAL</td>
                    <td style={{ textAlign: 'right' }}>{fmtInt(data.total_hours)} h</td>
                    <td></td>
                    <td style={{ textAlign: 'right', color: '#2E7D32' }}>{fmt(data.total_gross)} EUR</td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      </div>

      <p style={{ marginTop: 12, fontSize: '0.78rem', color: '#5E5F66' }}>
        Le calcul se base sur les heures de pointage <strong>approuvees</strong> du mois selectionne. Le taux horaire brut est renseigne sur la fiche de chaque salarie (fallback : tarif journalier / 7).
      </p>
    </div>
  );
};

export default PayrollVariablesPage;
